import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@toki/auth';
import { AsistenteChat } from '../components/AsistenteChat';
import { AdjuntosPendientes, BotonAdjuntar, capturasPegadas, sumarAdjuntos } from '../components/Adjuntar';
import { AdjuntosMensaje } from '../components/AdjuntosMensaje';
import { VisorImagenes } from '../components/VisorImagenes';
import {
  MAX_ADJUNTOS_PORTAL,
  createTicketAsClient,
  downloadTicketFile,
  esImagen,
  getAsistenteEstado,
  getMyThread,
  getMyTicket,
  listMyTickets,
  listCategories,
  replyAsClient,
  type PortalMessage,
  type PortalTicket,
  type TicketFile,
} from '../services/api';
import { tenantConfig } from '../tenant';
import styles from './Portal.module.css';

/**
 * EL PORTAL DEL CLIENTE, dentro de la app del admin.
 *
 * Hasta el paso 38 era una aplicación aparte (`apps/portal`, puerto 5177 en
 * dev, `/portal/` en producción). Ahora es una pantalla más de esta: lo que
 * decide qué ve cada uno es el ROL, no en qué servidor ha aterrizado. Así lo
 * pidió Jose —*«esa sea la página de login para todos y vean lo que tengan que
 * ver según su rol»*— y de paso desaparecen dos problemas que venían de tener
 * dos apps en el mismo origen: la paleta duplicada (K31) y el `localStorage`
 * compartido por convención (K24).
 *
 * ⚠️ Esta pantalla la ve quien NO es staff. La frontera de verdad no está
 * aquí: la pone el backend. `/incidencias/mine` devuelve solo lo de quien
 * pregunta y `_strip_internal` le quita los campos internos. Esto es
 * presentación — si algún día se pinta aquí algo que el backend no filtre, el
 * agujero es del backend, no de este fichero.
 *
 * Del documento de diseño (pestaña «Portal cliente») se porta lo que tiene
 * datos detrás. Fuera, en K29: el buscador de soluciones, la base de
 * conocimiento, «respuesta estimada hoy» (es SLA) y los estados «En espera» y
 * «Cerrado», que no existen en el grafo.
 */

/** Etiquetas de estado para el cliente. UN SOLO SITIO, igual que `roles.ts` y
 *  `priority.ts`. Son distintas de las del panel a propósito: al cliente no le
 *  dice nada «en_revision», y «rechazada» se lee como un portazo. */
const ESTADO: Record<string, string> = {
  nueva: 'Recibido',
  en_revision: 'En revisión',
  en_progreso: 'En curso',
  resuelta: 'Resuelto',
  rechazada: 'Cerrado',
};

/** Lo que ve el usuario en vez del nombre del técnico. El backend no manda
 *  `authorName` de staff a quien reporta (ver `_message_dict`), así que esto
 *  no es cosmética: es el rótulo que ocupa su lugar, y el mismo que firma los
 *  correos. */
const EQUIPO = 'Soporte';

function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function iniciales(nombre: string): string {
  return nombre.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

/** Lo que trae el formulario cuando el ticket se abre desde el asistente
 *  (paso 92a): la pregunta ya escrita y su id, que liga el KPI. */
type DesdeAsistente = { pregunta: string; preguntaId: string };

type Vista =
  | { name: 'lista' }
  | { name: 'detalle'; id: string }
  | { name: 'nuevo'; desde?: DesdeAsistente };

export function PortalScreen() {
  const { state, provider } = useAuth();
  const [vista, setVista] = useState<Vista>({ name: 'lista' });
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    provider.getAccessToken().then(setToken).catch(() => setToken(null));
  }, [provider, vista]);

  // El asistente del portal (92a): solo si está encendido. Si la consulta
  // falla, no se pinta — sin él el portal funciona igual. Va en un botón
  // FLOTANTE (D3, Jose, 2026-10-07): no mueve nada del portal de hoy, que se
  // rediseña en el paso 49.
  const [botActivo, setBotActivo] = useState(false);
  useEffect(() => {
    if (!token) return;
    getAsistenteEstado(token).then((e) => setBotActivo(e.activo)).catch(() => setBotActivo(false));
  }, [token]);
  const abrirDesdeBot = (pregunta: string, preguntaId: string) =>
    setVista({ name: 'nuevo', desde: { pregunta, preguntaId } });

  const nombre = state.user?.displayName ?? state.user?.email ?? '';

  return (
    <div className={styles.page}>
      {/* ── Cabecera oscura ────────────────────────────────── */}
      <header className={styles.top}>
        <div className={styles.topBar}>
          <img
            className={styles.logo}
            src={tenantConfig.logoOnDark}
            alt={tenantConfig.logoAlt}
          />
          <span className={styles.topWord}>Soporte</span>
          <div className={styles.spacer} />
          <span className={styles.who}>{nombre}</span>
          <span className={styles.avatar} aria-hidden>{iniciales(nombre)}</span>
          <button type="button" className={styles.salir} onClick={() => provider.logout()}>
            Salir
          </button>
        </div>

        {vista.name === 'lista' && (
          <div className={styles.hero}>
            <p className={styles.eyebrow}>Portal de soporte IT</p>
            <h1 className={styles.heroTitle}>¿En qué podemos ayudarte?</h1>
            {/* El horario es un dato fijo y cierto; el documento pone además un
                buscador de soluciones y un botón a la base de conocimiento, y
                ninguno de los dos existe (K29). */}
            <p className={styles.heroSub}>Soporte IT · lunes a viernes, 8:00–18:00</p>
            <button
              type="button"
              className={styles.cta}
              onClick={() => setVista({ name: 'nuevo' })}
            >
              Abrir un ticket
            </button>
          </div>
        )}
      </header>

      <main className={styles.body}>
        {token == null ? (
          <p className={styles.muted}>Cargando…</p>
        ) : vista.name === 'lista' ? (
          <Lista token={token} onAbrir={(id) => setVista({ name: 'detalle', id })} />
        ) : vista.name === 'nuevo' ? (
          <Nuevo
            // Una pregunta nueva desde el bot vuelve a montar el formulario.
            key={vista.desde?.preguntaId ?? 'nuevo'}
            token={token}
            desde={vista.desde}
            onHecho={(id) => setVista({ name: 'detalle', id })}
            onCancelar={() => setVista({ name: 'lista' })}
          />
        ) : (
          <Detalle token={token} id={vista.id} onVolver={() => setVista({ name: 'lista' })} />
        )}
      </main>

      {/* Fuera del formulario de alta: ahí ya se está abriendo el ticket. */}
      {token != null && botActivo && vista.name !== 'nuevo' && (
        <AsistenteChat token={token} onAbrirTicket={abrirDesdeBot} />
      )}
    </div>
  );
}

function Lista({ token, onAbrir }: { token: string; onAbrir: (id: string) => void }) {
  const [tickets, setTickets] = useState<PortalTicket[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMyTickets(token)
      .then((r) => setTickets(r.items))
      .catch((e: Error) => setError(e.message));
  }, [token]);

  if (error) return <p className={styles.error}>{error}</p>;
  if (!tickets) return <p className={styles.muted}>Cargando…</p>;

  const abiertos = tickets.filter((t) => t.status !== 'resuelta' && t.status !== 'rechazada').length;

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Mis tickets</h2>
        {abiertos > 0 && (
          <span className={styles.count}>{abiertos} {abiertos === 1 ? 'abierto' : 'abiertos'}</span>
        )}
      </div>

      {tickets.length === 0 ? (
        <p className={styles.empty}>
          Todavía no tienes ningún ticket. Pulsa «Abrir un ticket» o escribe a soporte
          por correo y aparecerá aquí.
        </p>
      ) : (
        <ul className={styles.list}>
          {tickets.map((t) => (
            <li key={t.id}>
              <button type="button" className={styles.row} onClick={() => onAbrir(t.id)}>
                <span className={styles.rowRef}>{t.reference}</span>
                <span className={styles.rowTitle}>{t.title}</span>
                <span className={`${styles.pill} ${styles[`st_${t.status}`] ?? ''}`}>
                  {ESTADO[t.status] ?? t.status}
                </span>
                <span className={styles.rowDate}>{fecha(t.createdAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Nuevo({
  token, desde, onHecho, onCancelar,
}: {
  token: string;
  desde?: DesdeAsistente;
  onHecho: (id: string) => void;
  onCancelar: () => void;
}) {
  // Desde el asistente, la pregunta ya escrita: el asunto, recortado a una
  // línea, y la descripción entera. El cliente elige la categoría y puede
  // cambiarlo todo.
  const [title, setTitle] = useState(() => {
    const p = desde?.pregunta ?? '';
    return p.length > 120 ? `${p.slice(0, 117)}…` : p;
  });
  const [description, setDescription] = useState(desde?.pregunta ?? '');
  const [category, setCategory] = useState('');
  const [cats, setCats] = useState<{ id: string; name: string; icon?: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /** Capturas y ficheros que salen con el ticket (paso 34, K25). */
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [avisoAdjuntos, setAvisoAdjuntos] = useState<string | null>(null);
  const añadir = (nuevos: File[]) => {
    const r = sumarAdjuntos(adjuntos, nuevos, MAX_ADJUNTOS_PORTAL);
    setAdjuntos(r.archivos);
    setAvisoAdjuntos(r.aviso);
  };

  useEffect(() => {
    // Las categorías son las mismas que usa el alta del panel: si el cliente
    // eligiera de una lista propia, el enrutado automático por categoría
    // dejaría de encontrar equipo.
    listCategories(token).then(setCats).catch(() => setCats([]));
  }, [token]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const t = await createTicketAsClient(token, {
        title: title.trim(),
        description: description.trim(),
        category,
        ...(desde ? { asistente_pregunta_id: desde.preguntaId } : {}),
      }, adjuntos);
      onHecho(t.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className={styles.card}>
      <button type="button" className={styles.back} onClick={onCancelar}>← Mis tickets</button>
      <h2 className={styles.cardTitle}>Abrir un ticket</h2>

      <form className={styles.form} onSubmit={enviar}>
        <label className={styles.field}>
          <span>¿Con qué necesitas ayuda?</span>
          <select
            className={styles.select}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
          >
            <option value="">Elige una categoría</option>
            {cats.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </label>

        <label className={styles.field}>
          <span>Asunto</span>
          <input
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Resume el problema en una línea"
            required
          />
        </label>

        <label className={styles.field}>
          <span>Descripción</span>
          <textarea
            className={styles.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="¿Qué ocurre, desde cuándo y qué has probado ya?"
            onPaste={(e) => {
              const pegadas = capturasPegadas(e);
              if (pegadas.length === 0) return;
              e.preventDefault();
              añadir(pegadas);
            }}
            rows={5}
            required
          />
        </label>

        {/* «Mándame una captura» sin salirse al correo (K25). */}
        <div className={styles.adjuntar}>
          <BotonAdjuntar conTexto disabled={enviando} onElegir={añadir} />
          <span className={styles.muted}>
            Capturas de pantalla, registros o documentos. Hasta {MAX_ADJUNTOS_PORTAL}.
          </span>
        </div>
        <AdjuntosPendientes
          archivos={adjuntos}
          aviso={avisoAdjuntos}
          onQuitar={(i) => { setAdjuntos((cur) => cur.filter((_, j) => j !== i)); setAvisoAdjuntos(null); }}
        />

        {/* El documento pone aquí un selector de urgencia («Puede esperar /
            Curso normal / Estoy parada»). NO se pinta: la prioridad la fija
            Kivai —en el alta manual la pone el técnico y en el correo se deduce
            por palabras clave—, y dejar que la elija quien abre el ticket
            convierte el campo en «todo el mundo está parado». Es una decisión
            de producto, no un olvido: ver K29. */}

        {error && <p className={styles.error} role="alert">{error}</p>}

        <div className={styles.formActions}>
          <button className={styles.cta} type="submit" disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar ticket'}
          </button>
        </div>
      </form>
    </section>
  );
}

function Detalle({ token, id, onVolver }: { token: string; id: string; onVolver: () => void }) {
  const [ticket, setTicket] = useState<PortalTicket | null>(null);
  const [mensajes, setMensajes] = useState<PortalMessage[]>([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [avisoAdjuntos, setAvisoAdjuntos] = useState<string | null>(null);
  /** La captura abierta en la caja de luz, o null. */
  const [visor, setVisor] = useState<number | null>(null);
  const añadir = (nuevos: File[]) => {
    const r = sumarAdjuntos(adjuntos, nuevos, MAX_ADJUNTOS_PORTAL);
    setAdjuntos(r.archivos);
    setAvisoAdjuntos(r.aviso);
  };

  const cargar = useCallback(() => {
    Promise.all([getMyTicket(token, id), getMyThread(token, id)])
      .then(([t, m]) => { setTicket(t); setMensajes(m); })
      .catch((e: Error) => setError(e.message));
  }, [token, id]);

  useEffect(cargar, [cargar]);

  const responder = async (e: FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await replyAsClient(token, id, texto.trim(), adjuntos);
      setTexto('');
      setAdjuntos([]);
      setAvisoAdjuntos(null);
      // Se recarga entero en vez de añadir el mensaje a mano: responder puede
      // REABRIR el ticket, y entonces el estado de la cabecera también cambia.
      cargar();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  if (error && !ticket) return <p className={styles.error}>{error}</p>;
  if (!ticket) return <p className={styles.muted}>Cargando…</p>;

  // Los adjuntos que le llegan son ya solo los que puede ver: el backend no
  // manda los de una nota interna (paso 34). Aquí solo se reparten.
  const files = ticket.files ?? [];
  const delAlta = files.filter((f) => !f.messageId);
  const porMensaje = new Map<string, TicketFile[]>();
  for (const f of files) {
    if (!f.messageId) continue;
    porMensaje.set(f.messageId, [...(porMensaje.get(f.messageId) ?? []), f]);
  }
  const imagenes = files.filter(esImagen);
  const abrirImagen = (f: TicketFile) => setVisor(Math.max(0, imagenes.findIndex((i) => i.id === f.id)));
  const descargar = (f: TicketFile) => {
    downloadTicketFile(ticket.id, f, token).catch((e: Error) => setError(e.message));
  };

  return (
    <section className={styles.card}>
      <button type="button" className={styles.back} onClick={onVolver}>← Mis tickets</button>

      <div className={styles.detHead}>
        <h2 className={styles.cardTitle}>{ticket.title}</h2>
        <span className={`${styles.pill} ${styles[`st_${ticket.status}`] ?? ''}`}>
          {ESTADO[ticket.status] ?? ticket.status}
        </span>
      </div>
      <p className={styles.detMeta}>
        <span className={styles.rowRef}>{ticket.reference}</span> · {ticket.category} ·
        {' '}Abierto el {fecha(ticket.createdAt)}
      </p>

      {delAlta.length > 0 && (
        <div className={styles.delAlta}>
          <span className={styles.delAltaRotulo}>Adjuntos al abrir el ticket</span>
          <AdjuntosMensaje
            incidenciaId={ticket.id}
            token={token}
            files={delAlta}
            onAbrirImagen={abrirImagen}
            onDescargar={descargar}
          />
        </div>
      )}

      <div className={styles.thread}>
        {mensajes.map((m) => (
          <div
            key={m.id}
            className={`${styles.msg} ${m.authorType === 'user' ? styles.msgMine : styles.msgStaff}`}
          >
            <div className={styles.msgFrom}>
              {m.authorType === 'user' ? 'Tú' : m.authorName || EQUIPO}
            </div>
            <div className={styles.msgBody}>{m.body}</div>
            <AdjuntosMensaje
              incidenciaId={ticket.id}
              token={token}
              files={porMensaje.get(m.id) ?? []}
              onAbrirImagen={abrirImagen}
              onDescargar={descargar}
            />
            <div className={styles.msgWhen}>{fecha(m.createdAt)}</div>
          </div>
        ))}
      </div>

      <form className={styles.form} onSubmit={responder}>
        <label className={styles.field}>
          <span>Responder</span>
          <textarea
            className={styles.textarea}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Escribe aquí tu respuesta…"
            onPaste={(e) => {
              const pegadas = capturasPegadas(e);
              if (pegadas.length === 0) return;
              e.preventDefault();
              añadir(pegadas);
            }}
            rows={4}
            // Solo una captura, sin texto, también vale.
            required={adjuntos.length === 0}
          />
        </label>
        <AdjuntosPendientes
          archivos={adjuntos}
          aviso={avisoAdjuntos}
          onQuitar={(i) => { setAdjuntos((cur) => cur.filter((_, j) => j !== i)); setAvisoAdjuntos(null); }}
        />
        {error && <p className={styles.error} role="alert">{error}</p>}
        <div className={styles.formActions}>
          <BotonAdjuntar conTexto disabled={enviando} onElegir={añadir} />
          <button
            className={styles.cta}
            type="submit"
            disabled={enviando || (!texto.trim() && adjuntos.length === 0)}
          >
            {enviando ? 'Enviando…' : 'Enviar respuesta'}
          </button>
          {ticket.status === 'resuelta' && (
            <span className={styles.muted}>
              Este ticket está resuelto: si respondes, se vuelve a abrir.
            </span>
          )}
        </div>
      </form>
      {visor !== null && imagenes.length > 0 && (
        <VisorImagenes
          incidenciaId={ticket.id}
          token={token}
          imagenes={imagenes}
          indice={visor}
          onIndice={setVisor}
          onCerrar={() => setVisor(null)}
        />
      )}
    </section>
  );
}
